import React, { useEffect, useRef } from "react";

import EmptyChart from "../../../../../../../../components/EmptyChart";
import Spinner from "../../../../../../../../components/Spinner";
// Config
import Config from "./Config";
import { dataseriesRangeOptions } from "./Options";

import { ChartOptions } from "../../../../../../schemas/report";

// Utils / Hooks
import useStore from "../../../../store";
import { ChartType as PYChart, ReportAnalyticsRbacPermissions } from "../../../../store/enums";

import { useGetReportDistributionData } from "../../../../../../api/hooks";

import { getColumnKeyFromColumnTypeValue } from "../../../../utils";

// Components
import BarChart from "../ChartHooks/BarChart/BarChart";
import useChartOptions, { Label } from "../ChartHooks/UseChartOptions/useChartOptions";
import { useDownloadImage } from "../ChartHooks/UseDownloadImage/useDownloadImage";
import { ChartContainer, VisualizationHeader } from "../Layout";
import css from "../Layout/Layout.module.scss";

type DistributionData = {
	min: number;
	max: number;
	count: number;
};

const gaussian = (x, mean, standardDeviation) => {
	const factor = 1 / (standardDeviation * Math.sqrt(2 * Math.PI));
	return factor * Math.exp(-0.5 * Math.pow((x - mean) / standardDeviation, 2));
};

const formatDataForHistogram = (data: DistributionData[]) => {
	const labels: Label[] = [];
	const datasets: number[] = [];
	const midPoints: number[] = [];

	if (!data) {
		return { labels, datasets };
	}

	data.forEach((item) => {
		const label =
			item.min === item.max ? `${item.min.toFixed(2)}` : `${item.min.toFixed(2)} - ${item.max.toFixed(2)}`;
		labels.push({ _id: label, name: label });
		datasets.push(item.count);
		midPoints.push((item.max + item.min) / 2);
	});

	const amplitude = Math.max(...datasets);
	const totalCount = datasets.reduce((acc, val) => acc + val, 0);
	const mean = midPoints.reduce((acc, midpoint, index) => acc + midpoint * datasets[index], 0) / totalCount;
	const variance =
		midPoints.reduce((acc, midpoint, index) => acc + datasets[index] * (midpoint - mean) ** 2, 0) / totalCount;
	const stdDev = Math.sqrt(variance);
	const gaussian_values = midPoints.map((x) => gaussian(x, mean, stdDev) * amplitude * 2);

	return { labels, datasets, gaussian_values, totalCount };
};

const ReportingHistogramChart = () => {
	const chartRef = useRef(null);
	const currentXAxisOptionRef = useRef<string | undefined>(undefined);

	const [getAppliedFilters, report, userPermissions] = useStore((state) => [
		state.getAppliedFilters,
		state.report,
		state.userPermissions,
	]);

	const {
		summarizeBySetting,
		visualizeOption,
		aggregationOperations,
		sortSettings,
		dataSeriesOptions,
		previousChart,
		previousVisualizeOption,
		anyAxisOptions,
		setVisualizeOptions,
	} = useChartOptions(PYChart.HistogramChart);

	const { downloadImage } = useDownloadImage(chartRef, {
		chartType: PYChart.HistogramChart,
		xAxisLabel: "Metric",
	});

	const xAxisOptions = anyAxisOptions;
	const columnParts = visualizeOption?.xAxis?.column.split("_") || [];
	const fieldTypeValue = columnParts[0] || "";
	const fieldValue = columnParts?.length > 0 ? columnParts.slice(1).join("_") : "";

	useEffect(() => {
		handleSetDefaultHistogramChartOptions();
	}, []);

	useEffect(() => {
		if (!anyAxisOptions?.some((option) => option.value === visualizeOption?.xAxis?.column)) {
			if (visualizeOption?.xAxis?.column) {
				handleSetDefaultHistogramChartOptions(true);
			}
		}
	}, [anyAxisOptions?.length]);

	const variables = {
		questionGroupingSessionID: report?.questionGroupingSessionID,
		columns: [visualizeOption?.xAxis?.column],
		filters: getAppliedFilters(),
		sorts: sortSettings,
		summarizeBy: summarizeBySetting,
		bucketsNum: visualizeOption?.binNum ? String(visualizeOption?.binNum) : Config.default_number_of_bins,
		selectedColumnAggregationOperation: aggregationOperations.find(
			(operation) =>
				operation.name === fieldValue && operation.type === getColumnKeyFromColumnTypeValue(fieldTypeValue)
		),
		min:
			visualizeOption?.xAxis?.min && currentXAxisOptionRef.current === visualizeOption?.xAxis?.column
				? String(visualizeOption?.xAxis?.min)
				: null, // Enforce min and max values if new column is selected
		max:
			visualizeOption?.xAxis?.max && currentXAxisOptionRef.current === visualizeOption?.xAxis?.column
				? String(visualizeOption?.xAxis?.max)
				: null,
	};

	const prefetchVariables: (typeof variables)[] = [];

	for (let i = 0; i < dataseriesRangeOptions.length; i++) {
		const binNum = dataseriesRangeOptions[i].value;
		if (
			userPermissions.includes(ReportAnalyticsRbacPermissions.CreateReport) &&
			JSON.stringify(binNum) !== variables.bucketsNum &&
			(i === 0 || JSON.stringify(binNum) !== JSON.stringify(dataseriesRangeOptions[i - 1].value))
		) {
			prefetchVariables.push({
				...variables,
				bucketsNum: binNum,
			});
		}
	}

	const handleSetDefaultHistogramChartOptions = (forceUpdate = false) => {
		const options: ChartOptions = {};

		if (dataSeriesOptions.length > 0) {
			options.dataSeries = {
				column:
					previousVisualizeOption?.dataSeries?.column ||
					visualizeOption?.dataSeries?.column ||
					dataSeriesOptions[dataSeriesOptions.length - 1].value,
			};
		}
		const columnX =
			previousVisualizeOption?.xAxis?.column || previousVisualizeOption?.yAxis?.column || xAxisOptions[0].value;
		const suggestedMinX = anyAxisOptions.find((option) => option.value === columnX)?.metadata?.min || null;
		const suggestedMaxX = anyAxisOptions.find((option) => option.value === columnX)?.metadata?.max || null;

		if (xAxisOptions.length > 0) {
			options.xAxis = {
				column: columnX,
				min: suggestedMinX,
				max: suggestedMaxX,
				hasSuggestedMin: !!suggestedMinX,
				hasSuggestedMax: !!suggestedMaxX,
			};
			if (forceUpdate) {
				options.xAxis.column = xAxisOptions[0].value;
			}
		}

		if (Object.keys(visualizeOption).length === 0 || forceUpdate) {
			const newVisualizeOptions = { [PYChart.HistogramChart]: { ...visualizeOption, ...options } };
			if (previousChart !== PYChart.HistogramChart) {
				newVisualizeOptions[previousChart] = null;
			}
			setVisualizeOptions(newVisualizeOptions, true);
			return;
		}

		// Current behavior: when there is visualization option on first render, if the selected datasereis columns doesn't exist, default to the first options
		if (Object.keys(visualizeOption).length > 0) {
			const hasDataSeriesColumn = dataSeriesOptions.some(
				(option) => option.value === visualizeOption.dataSeries?.column
			);
			const hasXAxisColumn = xAxisOptions.some((option) => option.value === visualizeOption.xAxis?.column);

			if (!hasDataSeriesColumn) {
				options.dataSeries = {
					column: dataSeriesOptions[dataSeriesOptions.length - 1].value,
				};
			}

			if (!hasXAxisColumn) {
				options.xAxis = {
					column: anyAxisOptions[0].value,
				};
			}

			const newVisualizeOption = { [PYChart.HistogramChart]: { ...visualizeOption, ...options } };
			setVisualizeOptions(newVisualizeOption);
		}
	};

	const { data: distributionData, isLoading } = useGetReportDistributionData(
		[variables, ...prefetchVariables],
		report?.type
	)[0];

	let distributionDataName = "reviewReportsDistribution";
	if (report?.type === "surveyReport") {
		distributionDataName = "surveyReportsDistribution";
	} else if (report?.type === "goalReport") {
		distributionDataName = "goalReportsDistribution";
	}

	const { labels, datasets, totalCount } = formatDataForHistogram(distributionData?.[distributionDataName]);

	let derivedMin, derivedMax;

	const hasData =
		Array.isArray(distributionData?.[distributionDataName]) && distributionData?.[distributionDataName].length > 0;

	if (hasData) {
		derivedMin = Math.floor(distributionData?.[distributionDataName]?.[0].min);
		derivedMax = Math.ceil(
			distributionData?.[distributionDataName]?.[distributionData?.[distributionDataName].length - 1].max
		);
	}

	// Inter default min and max values from the data when it's not previously set
	useEffect(() => {
		const isNewColumn = currentXAxisOptionRef.current !== visualizeOption?.xAxis?.column; // Check if the column has changed to default the min and max values

		if (hasData && isNewColumn) {
			// Create a deep copy of visualizeOption to modify
			// Some how using Object.assign then modify that object, it will still modify the visualize option!
			const existingOptions = JSON.parse(JSON.stringify(visualizeOption));
			const existingXAxis = existingOptions?.xAxis || ({} as any);
			const suggestedMinX =
				anyAxisOptions.find((option) => option.value === visualizeOption?.xAxis?.column)?.metadata?.min || null;
			const suggestedMaxX =
				anyAxisOptions.find((option) => option.value === visualizeOption?.xAxis?.column)?.metadata?.max || null;

			const hasSuggestedMaxX = existingOptions?.xAxis?.hasSuggestedMax;
			const hasSuggestedMinX = existingOptions?.xAxis?.hasSuggestedMin;

			if (suggestedMinX && !hasSuggestedMinX) {
				existingXAxis.min = suggestedMinX;
				existingXAxis.hasSuggestedMin = true;
			}
			if (suggestedMaxX && !hasSuggestedMaxX) {
				existingXAxis.max = suggestedMaxX;
				existingXAxis.hasSuggestedMax = true;
			}
			if (!hasSuggestedMinX && !suggestedMinX) {
				existingXAxis.min = derivedMin;
				existingXAxis.hasSuggestedMin = false;
			}
			if (!hasSuggestedMaxX && !suggestedMaxX) {
				existingXAxis.max = derivedMax;
				existingXAxis.hasSuggestedMax = false;
			}

			const inferredOptions = { ...existingOptions, xAxis: existingXAxis };
			let noChange = false;

			if (JSON.stringify(visualizeOption) === JSON.stringify(inferredOptions)) {
				noChange = true;
			}

			setVisualizeOptions(
				{
					[PYChart.HistogramChart]: {
						...existingOptions,
						xAxis: existingXAxis,
					},
				},
				noChange
			);

			currentXAxisOptionRef.current = visualizeOption?.xAxis?.column;
		}
	}, [visualizeOption?.xAxis?.column, derivedMin, derivedMax]);

	return (
		<ChartContainer padding="16px 24px 16px 0px" height="calc(100% - 5px)" width="calc(100% - 4px)">
			{isLoading && (
				<div className={css.spinner}>
					<Spinner />
				</div>
			)}
			{!isLoading && !hasData && <EmptyChart type={PYChart.HistogramChart} />}
			{!isLoading && hasData && (
				<>
					<VisualizationHeader
						viewingCount={totalCount}
						totalCount={totalCount}
						options={[
							{
								label: "Download Image",
								action: downloadImage,
							},
						]}
					/>
					<div className={css.relativeContainer}>
						<BarChart
							ref={chartRef}
							labels={labels}
							datasets={datasets}
							gaussianDataset={true} // is Histogram
							showBellCurve={
								visualizeOption?.showBellCurve === undefined ? true : visualizeOption?.showBellCurve
							}
							yLabelText={
								"# of " +
								dataSeriesOptions.find((option) => option.value === visualizeOption.dataSeries?.column)
									?.label +
								"s"
							}
							xLabelText={
								xAxisOptions.find((option) => option.value === visualizeOption.xAxis?.column)
									?.labelWithAggregation
							}
						/>
					</div>
				</>
			)}
		</ChartContainer>
	);
};

export default ReportingHistogramChart;
